// // MODIFIED
import { useState, useCallback, useEffect } from "react";
import leaveRequestService from "../services/leaveRequestService";
import { fileToBase64Old } from "../../utils/fileUtils";
import { REQUEST_TYPES } from "../../utils/enums";

export const useLeaveRequest = ({ employeePersonalInformationId, showGeneralMessage }) => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);

  const [formData, setFormData] = useState({
    leaveTypeId: "",
    dateFrom: "",
    dateTo: "",
    days: "",
    reason: "",
    isHalfDay: false,
    content: null,
    fileName: "",
    fileType: ""
  });

  const fetchHistory = useCallback(async () => {
    if (!employeePersonalInformationId) return;
    try {
      setLoading(true);
      const res = await leaveRequestService.getByEmployee(employeePersonalInformationId);
      setRequests(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [employeePersonalInformationId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // // MODIFIED: Logic to calculate days and enforce Half Day constraints
  useEffect(() => {
    if (formData.dateFrom && formData.dateTo) {
      const isSameDay = formData.dateFrom === formData.dateTo;

      // // ADDED: Force isHalfDay to false if dates are different
      const actualIsHalfDay = isSameDay ? formData.isHalfDay : false;

      const start = new Date(formData.dateFrom);
      const end = new Date(formData.dateTo);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

      setFormData(prev => ({
        ...prev,
        isHalfDay: actualIsHalfDay, // // MODIFIED: Sync state if dates changed
        days: actualIsHalfDay ? "0.5" : diffDays > 0 ? diffDays : 0
      }));
    }
  }, [formData.dateFrom, formData.dateTo, formData.isHalfDay]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (file) => {
    if (!file) return;

    const MAX_SIZE_MB = 2;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    if (file.size > MAX_SIZE_BYTES) {
      showGeneralMessage(`File is too large. Max size allowed is ${MAX_SIZE_MB}MB.`, false);
      setFormData(prev => ({ ...prev, content: null, fileName: "", fileType: "" }));
      return;
    }

    try {
      const base64 = await fileToBase64Old(file);
      setFormData(prev => ({ ...prev, content: base64, fileName: file.name, fileType: file.type }));
    } catch (error) {
      console.error("File conversion error:", error);
      showGeneralMessage("Failed to process file.", false);
    }
  };

  const submitRequest = async (formData, employeePersonalInformationId, employeeName) => {
    setLoading(true);

    const payload = {
      requestTypeId: REQUEST_TYPES.LEAVE_REQUEST_ID,
      leaveTypeId: parseInt(formData.leaveTypeId),
      dateFrom: formData.dateFrom,
      dateTo: formData.dateTo,
      days: parseFloat(formData.days),
      reason: formData.reason,
      isHalfDay: formData.isHalfDay,
      content: formData.content,
      fileName: formData.fileName,
      fileType: formData.fileType,
      employeePersonalInformationId: employeePersonalInformationId,
      dateRequested: new Date().toISOString(),
      status: "Sent",
      approverId: null,
      reasonForRejection: null,
      dateCompleted: null
    };

    try {
      const res = await leaveRequestService.create(payload);
      showGeneralMessage("Leave request submitted successfully!", true);
      fetchHistory();
      setFormData({
        leaveTypeId: "", dateFrom: "", dateTo: "", days: "",
        reason: "", isHalfDay: false, content: null, fileName: "", fileType: ""
      });
      return true;
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to submit request";
      showGeneralMessage(errorMsg, false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    requests,
    formData,
    setFormData,
    handleInputChange,
    handleFileChange,
    submitRequest,
    fetchHistory
  };
};